define('presentation/reactivation/viewModels/reactivationViewModel',[
    'businessServices/authentication/authenticationManager',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/authentication/sessionUserInfo',
    'businessServices/authentication/sessionWorkflowFlags',
    'businessServices/converters/currencyFormatter',
    'businessServices/login/accountStatusEvaluator',
    'common/promises/promiseFactory',
    'constants/accountStatusEnumerations',
    'constants/billingMethodConstants',
    'constants/creditCardResponseConstants',
    'plugins/router',
    'presentation/common/window/windowControl',
    'presentation/reactivation/facades/reactivationFacade',
    'presentation/reactivation/validators/reactivationValidator',
    'presentation/common/paymentMethod/viewModels/addPaymentMethodViewModel',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _i18n = require('i18next');
        const _accountStatusEvaluator = require('businessServices/login/accountStatusEvaluator');
        const _accountStatusEnumeration = require('constants/accountStatusEnumerations');
        const _authenticationManager = require('businessServices/authentication/authenticationManager');
        const _billingMethodConstants = require('constants/billingMethodConstants');
        const _creditCardResponse = require('constants/creditCardResponseConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('plugins/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _sessionUserInfo = require('businessServices/authentication/sessionUserInfo');
        const _sessionWorkflowFlags = require('businessServices/authentication/sessionWorkflowFlags');
        const _windowControl = require('presentation/common/window/windowControl');

        let _addPaymentMethodViewModel = null;
        let _facade = null;
        let _validator = null;
        let _availablePaymentMethods = null;

        const NEW_PAYMENT_METHOD =  {
            paymentMethodId: "new",
            description: _i18n.t("reactivation:newCard")
        };

        const _getAccountInformation = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.getAccountInformation()
                    .fail(deferredObject.reject)
                    .done((accountInformation) => {
                        self.planId(accountInformation.planId || null);
                        self.telephonyPackageId(accountInformation.telephonyPackageId || null);
                        self.liveReceptionistPackageId(accountInformation.liveReceptionistPackageId || null);
                        self.webChatPackageId(accountInformation.webChatPackageId || null);
                        self.outboundCallingPackageId(accountInformation.outboundCallingPackageId || null);

                        const accountFraudStatus = accountInformation.fraudStatus;
                        const accountFlags = accountInformation.flags;

                        if (accountFlags.doNotReactivate) {
                            self.isAccountAbleToBeActivated(false);
                        } else {
                            switch (accountFraudStatus) {
                                case _accountStatusEnumeration.fraudStatus.ok:
                                case _accountStatusEnumeration.fraudStatus.fraudReview:
                                    self.isAccountAbleToBeActivated(true);
                                    break;
                                default:
                                    self.isAccountAbleToBeActivated(false);
                                    break;
                            }
                        }
                        deferredObject.resolve();
                    });
            });
        };

        const _getBalanceInformation = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.getBalanceInformation()
                    .fail(deferredObject.reject)
                    .done((balanceDue) => {
                        const hasPastDueBalance = balanceDue > 0;
                        self.hasPastDueBalance(hasPastDueBalance);
                        self.balanceDue(balanceDue);
                        deferredObject.resolve();
                    });
            });
        };

        const _getReactivationInformation = () => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.getReactivationInformation()
                    .fail(deferredObject.reject)
                    .done((reactivationInformation) => {
                        self.isAccountReinstatement(reactivationInformation.isAccountReinstatement);
                        self.billingMethod(reactivationInformation.billingMethod || _billingMethodConstants.CreditCard);
                        if (self.billingMethod() === _billingMethodConstants.CreditCard) {
                            _facade.getAllPaymentMethods()
                                .fail(deferredObject.reject)
                                .done((paymentMethods) => {
                                    _availablePaymentMethods = paymentMethods.filter((paymentMethod) => {
                                        return paymentMethod.isExpired() === false;
                                    });
                                    _buildPaymentMethodsDropDownOptions()
                                        .fail(deferredObject.reject)
                                        .done((paymentMethodDropDownOptions) => {
                                            self.paymentMethodDropDownOptions(paymentMethodDropDownOptions);
                                            _setSelectedPaymentMethod();
                                            deferredObject.resolve();
                                        });
                                });
                        } else {
                            deferredObject.resolve();
                        }
                    });
            });
        };

        const _buildPaymentMethodsDropDownOptions = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethodDropDownOptions = _availablePaymentMethods.map((paymentMethod) => {
                    return {
                        paymentMethodId: paymentMethod.paymentMethodId,
                        cardHolderName: paymentMethod.cardHolderName,
                        cardType: paymentMethod.cardType,
                        expirationDate: paymentMethod.expirationDate,
                        country: paymentMethod.country,
                        postalCode: paymentMethod.postalCode,
                        description: paymentMethod.description
                    };
                });

                _facade.getAddPaymentMethodAllowed()
                    .fail(deferredObject.reject)
                    .done((addPaymentMethodAllowed) => {
                        if (addPaymentMethodAllowed) {
                            paymentMethodDropDownOptions.push(NEW_PAYMENT_METHOD);
                        }
                        deferredObject.resolve(paymentMethodDropDownOptions);
                    });
            });
        };

        const _setSelectedPaymentMethod = () => {
            let defaultPaymentMethod;
            defaultPaymentMethod = _availablePaymentMethods.find((paymentMethod) => {
                return paymentMethod.isDefault();
            });
            if (defaultPaymentMethod === undefined) {
                self.selectedPaymentMethod(NEW_PAYMENT_METHOD);
            } else {
                self.selectedPaymentMethod(defaultPaymentMethod);
            }
        };

        const _validateReactivation = () => {
            return _promiseFactory.defer((deferredObject) => {
                _validator.validate()
                    .fail(deferredObject.reject)
                    .done((reactivationIsValid) => {
                        if (reactivationIsValid === false) {
                            self.showValidation(true);
                        }
                        deferredObject.resolve(reactivationIsValid);
                    });
            });
        };

        const _addOrEditPaymentMethod = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethodPromiseFactory = new PromiseFactoryConstructor();
                let addPaymentMethodIsValid = false;
                let reactivationIsValid = false;

                if (self.isNewPaymentMethod()) {
                    paymentMethodPromiseFactory.defer((newPaymentDeferred) => {
                        self.addPaymentMethodViewModel().validate()
                            .fail(newPaymentDeferred.reject)
                            .done((isValid) => {
                                addPaymentMethodIsValid = isValid;
                                newPaymentDeferred.resolve();
                            });
                    });
                } else {
                    addPaymentMethodIsValid = true;
                }

                paymentMethodPromiseFactory.defer((validatorDeferred) => {
                    _validator.validate()
                        .fail(validatorDeferred.reject)
                        .done((isValid) => {
                            reactivationIsValid = isValid;
                            validatorDeferred.resolve();
                        });
                });

                paymentMethodPromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (addPaymentMethodIsValid && reactivationIsValid) {
                            let promise;
                            if (self.isNewPaymentMethod()) {
                                promise = _addPaymentMethodAndMakeDefault();
                            } else {
                                promise = _setDefaultPaymentMethod(self.selectedPaymentMethod().paymentMethodId);
                            }
                            promise
                                .fail(deferredObject.reject)
                                .done((paymentMethodUpdated) => {
                                    if (paymentMethodUpdated) {
                                        deferredObject.resolve(paymentMethodUpdated);
                                    } else {
                                        self.showReactivationFailureMessage(true);
                                        deferredObject.resolve(false);
                                    }
                                });
                        } else {
                            self.showValidation(true);
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _addPaymentMethodAndMakeDefault = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethod = _addPaymentMethodViewModel.paymentMethod();
                _facade.addPaymentMethodAndMakeDefault(paymentMethod)
                    .fail(deferredObject.reject)
                    .done((addPaymentMethodAndMakePaymentResponse) => {
                        if (_validatePaymentProcessing(addPaymentMethodAndMakePaymentResponse.status)) {
                            let paymentMethodPresentationObject = _facade.buildPaymentMethodPresentationObject(addPaymentMethodAndMakePaymentResponse.paymentMethod);
                            self.selectedPaymentMethod(paymentMethodPresentationObject);
                            deferredObject.resolve(true);
                        } else {
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _setDefaultPaymentMethod = (paymentMethodId) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.setDefaultPaymentMethod(paymentMethodId)
                    .fail(deferredObject.reject)
                    .done((setDefaultPaymentMethodResponse) => {
                        deferredObject.resolve(true);
                    });
            });
        };

        const _addPaymentMethodAndMakePayment = () => {
            return _promiseFactory.defer((deferredObject) => {
                let paymentMethod = _addPaymentMethodViewModel.paymentMethod();
                _facade.addPaymentMethodAndMakePayment(paymentMethod, self.balanceDue())
                    .fail(deferredObject.reject)
                    .done((addPaymentMethodAndMakePaymentResponse) => {
                        if (_validatePaymentProcessing(addPaymentMethodAndMakePaymentResponse.status)) {
                            let paymentMethodPresentationObject = _facade.buildPaymentMethodPresentationObject(addPaymentMethodAndMakePaymentResponse.paymentMethod);
                            self.selectedPaymentMethod(paymentMethodPresentationObject);
                            deferredObject.resolve(true);
                        } else {
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _makePayment = (paymentMethodId) => {
            return _promiseFactory.defer((deferredObject) => {
                _facade.makePayment(paymentMethodId, self.balanceDue().toString())
                    .fail(deferredObject.reject)
                    .done((paymentResults) => {
                        if (_validatePaymentProcessing(paymentResults.status)) {
                            let selectedPaymentMethod = self.selectedPaymentMethod();
                            self.selectedPaymentMethod(selectedPaymentMethod);
                            deferredObject.resolve(true);
                        } else {
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _payBalance = () => {
            return _promiseFactory.defer((deferredObject) => {
                let payNowPromiseFactory = new PromiseFactoryConstructor();
                let addPaymentMethodIsValid = false;
                let reactivationIsValid = false;

                if (self.isNewPaymentMethod()) {
                    payNowPromiseFactory.defer((newPaymentDeferred) => {
                        self.addPaymentMethodViewModel().validate()
                            .fail(newPaymentDeferred.reject)
                            .done((isValid) => {
                                addPaymentMethodIsValid = isValid;
                                newPaymentDeferred.resolve();
                            });
                    });
                } else {
                    addPaymentMethodIsValid = true;
                }

                payNowPromiseFactory.defer((validatorDeferred) => {
                    _validator.validate()
                        .fail(validatorDeferred.reject)
                        .done((isValid) => {
                            reactivationIsValid = isValid;
                            validatorDeferred.resolve();
                        });
                });

                payNowPromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (addPaymentMethodIsValid && reactivationIsValid) {
                            let promise;
                            if (self.isNewPaymentMethod()) {
                                promise = _addPaymentMethodAndMakePayment();
                            } else {
                                promise = _makePayment(self.selectedPaymentMethod().paymentMethodId);
                            }
                            promise
                                .fail(deferredObject.reject)
                                .done((paymentProcessed) => {
                                    if (paymentProcessed) {
                                        deferredObject.resolve(paymentProcessed);
                                    } else {
                                        self.showReactivationFailureMessage(true);
                                        deferredObject.resolve(false);
                                    }
                                });
                        } else {
                            self.showValidation(true);
                            deferredObject.resolve(false);
                        }
                    });
            });
        };

        const _reactivateAccount = () => {
            return _promiseFactory.defer((deferredObject) => {
                let reactivationPromiseFactory = new PromiseFactoryConstructor();
                let addPaymentMethodIsValid = false;
                let reactivationIsValid = false;

                if (self.isNewPaymentMethod()) {
                    reactivationPromiseFactory.defer((newPaymentDeferred) => {
                        self.addPaymentMethodViewModel().validate()
                            .fail(newPaymentDeferred.reject)
                            .done((isValid) => {
                                addPaymentMethodIsValid = isValid;
                                newPaymentDeferred.resolve();
                            });
                    });
                } else {
                    addPaymentMethodIsValid = true;
                }

                reactivationPromiseFactory.defer((validatorDeferred) => {
                    _validator.validate()
                        .fail(validatorDeferred.reject)
                        .done((isValid) => {
                            reactivationIsValid = isValid;
                            validatorDeferred.resolve();
                        });
                });

                reactivationPromiseFactory.wait()
                    .fail(deferredObject.reject)
                    .done(() => {
                        if (addPaymentMethodIsValid && reactivationIsValid) {
                            let paymentMethodId = null;
                            let newPaymentMethod = null;
                            if (self.isBilledByCreditCard()) {
                                paymentMethodId = self.isNewPaymentMethod() ? 0 : self.selectedPaymentMethod().paymentMethodId;
                                newPaymentMethod = self.isNewPaymentMethod() ? self.addPaymentMethodViewModel().paymentMethod() : self.selectedPaymentMethod();
                            }
                            const reactivationData = {
                                planId: self.planId(),
                                telephonyPackageId: self.telephonyPackageId(),
                                liveReceptionistPackageId: self.liveReceptionistPackageId(),
                                webChatPackageId: self.webChatPackageId(),
                                outboundCallingPackageId: self.outboundCallingPackageId(),
                                paymentMethodId: paymentMethodId,
                                newPaymentMethod: newPaymentMethod
                            };
                            _facade.reactivateAccount(reactivationData)
                                .fail(deferredObject.reject)
                                .done((reactivationResult) => {
                                    if (_validatePaymentProcessing(reactivationResult.status)) {
                                        deferredObject.resolve(true);
                                    } else {
                                        self.showReactivationFailureMessage(true);
                                        deferredObject.resolve(false);
                                    }
                                });
                        } else {
                            self.showValidation(true);
                            deferredObject.resolve(false);
                        }
                    });
            });
        };


        const _validatePaymentProcessing = (paymentStatus) => {
            switch (paymentStatus) {
                case _creditCardResponse.success:
                case _creditCardResponse.approved:
                    self.paymentFailureTitle('');
                    self.paymentFailureMessage('');
                    return true;
                case _creditCardResponse.declined:
                    self.paymentFailureTitle(_i18n.t('reactivation:declinedTitle'));
                    self.paymentFailureMessage(_i18n.t('reactivation:declinedMessage'));
                    return false;
                case _creditCardResponse.duplicateCard:
                case _creditCardResponse.invalidCard:
                case _creditCardResponse.invalidConnection:
                    self.paymentFailureTitle(_i18n.t('reactivation:genericFailureTitle'));
                    self.paymentFailureMessage(_i18n.t('reactivation:genericFailureMessage'));
                    return false;
                default:
                    throw new Error(paymentStatus);
            }
        };

        self.isCompositionComplete = ko.observable(false);
        self.isSubmitInProgress = ko.observable(false);
        self.isAccountReinstatement = ko.observable(false);
        self.addPaymentMethodViewModel = ko.observable();
        self.isBalancePaid = ko.observable(false);
        self.isAccountReactivated = ko.observable(false);
        self.balancePaidAmount = ko.observable('');
        self.customerAgreesToPay = ko.observable(false);
        self.paymentMethodDropDownOptions = ko.observableArray([]);
        self.selectedPaymentMethod = ko.observable('');
        self.isCreditCardDeclined = ko.observable(true);
        self.balanceDue = ko.observable(null);
        self.hasPastDueBalance = ko.observable(false);
        self.billingMethod = ko.observable(_billingMethodConstants.CreditCard);
        self.showValidation = ko.observable(false);
        self.showReactivationFailureMessage = ko.observable(false);
        self.paymentFailureTitle = ko.observable('');
        self.paymentFailureMessage = ko.observable('');
        self.isAccountAbleToBeActivated = ko.observable(true);
        self.planId = ko.observable(null);
        self.telephonyPackageId = ko.observable(null);
        self.liveReceptionistPackageId = ko.observable(null);
        self.webChatPackageId = ko.observable(null);
        self.outboundCallingPackageId = ko.observable(null);

        self.shouldDisplayAgentBypassButton = ko.pureComputed(() => {
            return _sessionAccountInfo.isImpersonating();
        });

        self.isAccountAbleToChangePlans = ko.pureComputed(() => {
            return !self.isAccountReinstatement();
        });

        self.isBilledByCreditCard = ko.pureComputed(() => self.billingMethod() === _billingMethodConstants.CreditCard);

        self.shouldContactUsToReactivate = ko.pureComputed(() => {
            return self.isBilledByCreditCard() === false && self.hasPastDueBalance();
        });

        self.isNewPaymentMethod = ko.pureComputed(() => {
            if (self.isBilledByCreditCard() === false) {
                return false;
            }
            return self.selectedPaymentMethod().paymentMethodId === NEW_PAYMENT_METHOD.paymentMethodId;
        });

        self.headerTitle = ko.pureComputed(() => {
            if (self.hasPastDueBalance()) {
                return _i18n.t("reactivation:warningTitlePastDueBalance", {
                    firstName: _sessionUserInfo.firstName(),
                    pastDueBalance: _currencyFormatter.formatUSDForDisplay(self.balanceDue())
                });
            }
            return _i18n.t("reactivation:welcomeTitle", {
                firstName: _sessionUserInfo.firstName()
            });
        });

        self.headerMessage = ko.pureComputed(() => {
            if (self.shouldContactUsToReactivate()) {
                return "";
            }
            if (self.isBilledByCreditCard() === false && self.isAccountAbleToBeActivated()) {
                if (self.isAccountReinstatement()) {
                    return _i18n.t("reactivation:welcomeMessageNoCardReinstatement");
                }
                return _i18n.t("reactivation:welcomeMessageNoCard");
            }
            if (self.isAccountAbleToBeActivated() && self.isAccountReinstatement()) {
                return _i18n.t("reactivation:welcomeMessageReinstatement");
            } else if (self.isAccountAbleToBeActivated()) {
                return _i18n.t("reactivation:welcomeMessage");
            } else {
                return _i18n.t("reactivation:warningMessageCannotReactivate");
            }
        });

        self.reactivationButtonLabel = ko.pureComputed(() => {
            if (self.isAccountReinstatement()) {
                return _i18n.t("reactivation:reactivateAccount");
            } else {
                return _i18n.t("continue");
            }
        });

        self.payAndReactivateButtonLabel = ko.pureComputed(() => {
            if (self.isAccountReinstatement()) {
                return _i18n.t("reactivation:payBalanceAndReactivateAccount");
            } else {
                return _i18n.t("reactivation:payBalanceAndContinue");
            }
        });

        self.customerAgreesToPayAcknowledgement = ko.pureComputed(() => {
            return _i18n.t("reactivation:customerAgreesToPayAcknowledgement", {
                pastDueBalance: _currencyFormatter.formatUSDForDisplay(self.balanceDue())
            });
        });

        self.customerAgreesToPayIsValid = ko.pureComputed(() => {
            if (self.showValidation() && self.hasPastDueBalance()) {
                return self.customerAgreesToPay();
            }
            return true;
        });

        self.balancePaidMessage = ko.pureComputed(() => {
            return _i18n.t("reactivation:balancePaidMessage", {
                paymentAmount: self.balancePaidAmount()
            });
        });

        self.balancePaidNextStepMessage = ko.pureComputed(() => {
            if (self.isAccountReactivated()) {
                return _i18n.t("reactivation:balancePaidMessageReactivated");
            }
            return _i18n.t("reactivation:balancePaidMessageChoosePlan");
        });

        self.openTermsOfAgreement = () => {
            _windowControl.openTermsOfAgreement();
        };

        self.onContinue = () => {
            if (self.isAccountReactivated()) {
                _router.navigate(_navigationConfiguration.loginContinuationPrompt);
                return;
            }
            _router.navigate(_navigationConfiguration.routesById.reactivationPlans.url);
        };

        self.onCancel = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _authenticationManager.logout()
                .done(() => {
                    _router.navigate(_navigationConfiguration.loginPageUrl);
                });
        };

        self.onAgentBypass = () => {
            if (self.isSubmitInProgress()) {
                return;
            }

            _sessionWorkflowFlags.hasBypassedClosedAccount(true);
            _sessionWorkflowFlags.hasBypassedPastDueBalance(true);
            _router.navigate(_navigationConfiguration.loginLandingPageUrl);
        };

        self.reactivateAccount = () => {
            self.showValidation(false);
            self.showReactivationFailureMessage(false);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                if (self.isAccountReinstatement()) {
                    _reactivateAccount()
                        .fail(deferredObject.reject)
                        .done((reactivationSuccessful) => {
                            if (reactivationSuccessful) {
                                _accountStatusEvaluator.refresh();
                                _router.navigate(_navigationConfiguration.loginLandingPageUrl);
                                deferredObject.resolve();
                            } else {
                                deferredObject.resolve();
                            }
                        });
                } else if (self.isBilledByCreditCard() === false) {
                    _validateReactivation()
                        .fail(deferredObject.reject)
                        .done((reactivationIsValid) => {
                            if (reactivationIsValid) {
                                _router.navigate(_navigationConfiguration.routesById.reactivationPlans.url);
                            }
                            deferredObject.resolve();
                        });
                }  else {
                    _addOrEditPaymentMethod()
                        .fail(deferredObject.reject)
                        .done((addOrEditPaymentMethodSuccessful) => {
                            if (addOrEditPaymentMethodSuccessful) {
                                _router.navigate(_navigationConfiguration.routesById.reactivationPlans.url);
                                deferredObject.resolve();
                            } else {
                                deferredObject.resolve();
                            }
                        });
                }
            });
        };

        self.payBalanceAndReactivateAccount = () => {
            self.showValidation(false);
            self.showReactivationFailureMessage(false);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                _payBalance()
                    .fail(deferredObject.reject)
                    .done((paymentProcessed) => {
                        if (paymentProcessed) {
                            if (self.isAccountReinstatement()) {
                                _reactivateAccount()
                                    .fail(deferredObject.reject)
                                    .done(() => {
                                        self.balancePaidAmount(_currencyFormatter.formatUSDForDisplay(self.balanceDue()));
                                        _accountStatusEvaluator.refresh();
                                        self.isAccountReactivated(true);
                                        self.isBalancePaid(true);
                                        deferredObject.resolve();
                                    });
                            } else {
                                self.balancePaidAmount(_currencyFormatter.formatUSDForDisplay(self.balanceDue()));
                                _accountStatusEvaluator.refresh();
                                self.isAccountReactivated(false);
                                self.isBalancePaid(true);
                                deferredObject.resolve();
                            }
                        } else {
                            deferredObject.resolve();
                        }
                    });
            });
        };

        self.payBalanceAndKeepAccountClosed = () => {
            self.showValidation(false);
            self.showReactivationFailureMessage(false);
            return _promiseFactory.deferWithMinimumWait((deferredObject) => {
                _payBalance()
                    .fail(deferredObject.reject)
                    .done((paymentProcessed) => {
                        if (paymentProcessed) {
                            self.balancePaidAmount(_currencyFormatter.formatUSDForDisplay(self.balanceDue()));
                            _accountStatusEvaluator.refresh();
                            self.isAccountReactivated(false);
                            self.isBalancePaid(true);
                            deferredObject.resolve();
                        } else {
                            deferredObject.resolve();
                        }
                    });
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/reactivation/facades/reactivationFacade');
            _facade = new FacadeConstructor();
            _facade.init();

            const ValidatorConstructor = require('presentation/reactivation/validators/reactivationValidator');
            _validator = new ValidatorConstructor();

            const AddPaymentMethodViewModel = require('presentation/common/paymentMethod/viewModels/addPaymentMethodViewModel');
            _addPaymentMethodViewModel = new AddPaymentMethodViewModel();
            self.addPaymentMethodViewModel(_addPaymentMethodViewModel);

            return _initialize();
        };

        const _initialize = () => {
            _getBalanceInformation();
            _getAccountInformation();
            _getReactivationInformation();

            _validator.registerViewModel(self);

            return _promiseFactory.wait();
        };
    };
});
